import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Link } from '@heroui/react';
import { Link as RouterLink } from 'react-router-dom';
import ProtectedAdminOnlyRoute from '../ProtectedAdminOnlyRoute';
import { Fragment } from 'react';

interface Tab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  Component: React.ComponentType;
  isAdminOnly?: boolean;
}

interface AdminLayoutProps {
  tabs: Tab[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ tabs }) => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const activeTab = location.pathname.split('/').pop();
  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-background border-r border-gray-200 dark:border-gray-700 min-h-screen">
          <nav className="p-6" data-tour="admin-tabs">
            <div className="space-y-2">
              {tabs.map((tab) => (
                <Link
                  as={RouterLink}
                  key={tab.id}
                  to={`/admin/${tab.id}`}
                  className={cn(
                    'w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors',
                    activeTab === tab.id
                      ? 'bg-blue-50 text-[#667eea] border border-blue-200'
                      : 'text-foreground hover:bg-gray-50 dark:hover:bg-gray-700',
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{t(tab.label)}</span>
                </Link>
              ))}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <AnimatePresence mode="wait" key={activeTab}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Routes>
                {tabs.map((tab) => {
                  const Wrapper = tab.isAdminOnly ? ProtectedAdminOnlyRoute : Fragment;
                  return (
                    <Route
                      key={tab.id}
                      path={`/${tab.id}`}
                      element={
                        <Wrapper>
                          <tab.Component />
                        </Wrapper>
                      }
                    />
                  );
                })}
                <Route path="*" element={<Navigate to={`/admin/${tabs[0]?.id || 'products'}`} replace />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
